<template>
  <div v-if="user">
    <div v-if="isLoading" class="has-text-centered">
      <p>Loading sent emails...</p>
    </div>
    <div v-else-if="sentEmails.length === 0" class="has-text-centered">
      <p>No sent emails found.</p>
    </div>
    <div v-else>
      <table class="table is-fullwidth is-striped">
        <thead>
          <tr>
            <th>Sent At</th>
            <th>Template Name</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="email in sentEmails" :key="email.id">
            <td>{{ formatDate(email.created_at) }}</td>
            <td>{{ email.template_name }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination is-centered mt-4">
        <a
          class="pagination-previous"
          :disabled="!hasPreviousPage"
          @click="hasPreviousPage && changePage(paginationPrevious)"
        >
          Previous
        </a>
        <a
          class="pagination-next"
          :disabled="!hasNextPage"
          @click="hasNextPage && changePage(paginationNext)"
        >
          Next
        </a>
        <ul class="pagination-list">
          <li v-for="page in displayedPages" :key="page">
            <a
              v-if="page !== '...'"
              :class="[
                'pagination-link',
                currentPage === page ? 'is-current' : '',
              ]"
              @click="changePage(page)"
            >
              {{ page }}
            </a>
            <span v-else class="pagination-ellipsis">&hellip;</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import endpoints from "@/api/endpoints";
import { formatDate } from "@/utils/date_converter";

export default {
  name: "UserSentEmailsTab",
  props: {
    userData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      sentEmails: [],
      isLoading: false,
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      totalItems: 0,
      paginationPrevious: null,
      paginationNext: null,
    };
  },
  computed: {
    ...mapState(["selectedUser"]),
    user() {
      return this.userData || this.selectedUser;
    },
    displayedPages() {
      if (this.totalPages <= 7) {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }

      const pages = [];
      pages.push(1);

      if (this.currentPage > 3) {
        pages.push("...");
      }

      const startPage = Math.max(2, this.currentPage - 1);
      const endPage = Math.min(this.totalPages - 1, this.currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (this.currentPage < this.totalPages - 2) {
        pages.push("...");
      }

      if (this.totalPages > 1) {
        pages.push(this.totalPages);
      }

      return pages;
    },
    hasPreviousPage() {
      return this.paginationPrevious !== null && this.paginationPrevious < this.currentPage;
    },
    hasNextPage() {
      return this.paginationNext !== null && this.paginationNext > this.currentPage;
    },
  },
  methods: {
    formatDate,
    async loadSentEmails(page = 1, size = 10) {
      if (!this.user || !this.user.id) return;

      this.isLoading = true;

      try {
        const response = await this.$http.get(endpoints.SENT_EMAILS, {
          params: {
            user_id: this.user.id,
            page: page,
            size: size,
          },
        });

        if (response && response.data && response.data.data) {
          this.sentEmails = response.data.data;
          this.totalItems = response.data.total_count || 0;
          this.totalPages = response.data.last || 1;
          this.currentPage = page;
          this.pageSize = size;
          this.paginationPrevious = response.data.previous || null;
          this.paginationNext = response.data.next || null;
        } else {
          this.sentEmails = [];
          this.totalPages = 1;
          this.currentPage = 1;
          this.paginationPrevious = null;
          this.paginationNext = null;
        }
      } catch (error) {
        console.error("Error loading sent emails:", error);
        this.sentEmails = [];
        this.totalPages = 1;
        this.currentPage = 1;
        this.paginationPrevious = null;
        this.paginationNext = null;
      } finally {
        this.isLoading = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.loadSentEmails(page, this.pageSize);
      }
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser && newUser.id) {
          this.loadSentEmails(1, this.pageSize);
        } else {
          this.sentEmails = [];
          this.currentPage = 1;
          this.totalPages = 1;
          this.paginationPrevious = null;
          this.paginationNext = null;
        }
      },
    },
  },
};
</script>

<style scoped>
.table {
  margin-bottom: 0;
}

.pagination-link {
  cursor: pointer;
}

.pagination-link.is-current {
  background-color: #3273dc;
  border-color: #3273dc;
  color: #fff;
}

.pagination-previous,
.pagination-next {
  cursor: pointer;
}

.pagination-previous[disabled],
.pagination-next[disabled] {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
